// client/src/coms/serveradmin/CommunityFrame.js
// Wraps a server's chat / voice view: shows the onboarding screen instead when the member still has to
// onboard (or waits for approval), and private notices from the server (approvals, automation messages) as
// dismissible cards. When nothing applies it renders the view unchanged.
import React, { useCallback, useEffect, useState } from 'react';
import { comsApiGet, comsApiPost } from '../comsApi';
import OnboardingScreen from './OnboardingScreen';
import './admin.css';

function ServerNotices({ serverKey }) {
  const [notices, setNotices] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = () =>
      comsApiGet(`/me/notices?server=${encodeURIComponent(serverKey)}`)
        .then((r) => alive && setNotices((r && r.notices) || []))
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [serverKey]);
  if (!notices.length) return null;
  return (
    <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 3000, width: 'min(380px, calc(100vw - 32px))', display: 'flex', flexDirection: 'column', gap: 8 }} role="region" aria-label="Messages from this server">
      {notices.slice(0, 3).map((n) => (
        <div key={n.id} className="vxa-notice" style={{ margin: 0, background: 'var(--vx-elevated, #313338)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }} role="status">
          <span aria-hidden="true">✉️</span>
          <div style={{ minWidth: 0 }}>
            <b>{n.title || n.server.name}</b>
            <div style={{ whiteSpace: 'pre-wrap' }}>{n.body}</div>
            <div className="vxa-muted vxa-small">From {n.server.name}</div>
          </div>
          <button
            type="button"
            className="vx-btn small"
            onClick={() => {
              setNotices((list) => list.filter((x) => x.id !== n.id));
              comsApiPost(`/me/notices/${n.id}/read`, {}).catch(() => {});
            }}
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}

export default function CommunityFrame({ serverKey, children }) {
  const [ob, setOb] = useState(null);
  const load = useCallback(() => {
    if (!serverKey) return Promise.resolve();
    return comsApiGet(`/servers/${encodeURIComponent(serverKey)}/onboarding`)
      .then((r) => {
        setOb((prev) => {
          // Approved / verified while watching: the channel list and permissions change.
          if (prev && prev.show && !r.show) window.dispatchEvent(new CustomEvent('vox-permissions-changed'));
          return r;
        });
      })
      .catch(() => setOb(null));
  }, [serverKey]);

  useEffect(() => {
    setOb(null);
    load();
  }, [load]);

  useEffect(() => {
    const on = () => load();
    window.addEventListener('vox-permissions-changed', on);
    // Members waiting for approval cannot open any channel stream, so check now and then.
    const t = ob && ob.state && ob.state.status === 'pending' ? setInterval(load, 20000) : null;
    return () => {
      window.removeEventListener('vox-permissions-changed', on);
      if (t) clearInterval(t);
    };
  }, [load, ob]);

  if (ob && ob.show) {
    return (
      <div className="coms-view" style={{ position: 'relative', overflowY: 'auto', minHeight: 0 }} data-testid="onboarding-gate">
        <div className="vxa-onboard" style={{ position: 'static', minHeight: '100%' }}>
          <OnboardingScreen
            serverKey={serverKey}
            data={ob}
            onDone={() => {
              load();
              window.dispatchEvent(new CustomEvent('vox-permissions-changed'));
              window.dispatchEvent(new CustomEvent('vox-channels-changed'));
            }}
            onOpenChannel={(id) => window.dispatchEvent(new CustomEvent('vox-open-target', { detail: { kind: 'channel', server_key: serverKey, channel_id: id, channel_kind: 'text' } }))}
          />
        </div>
      </div>
    );
  }
  return (
    <>
      {children}
      {serverKey ? <ServerNotices serverKey={serverKey} /> : null}
    </>
  );
}
